import { Injectable } from '@angular/core';
import {
  catchAndContinue,
  convertEquipmentNumberToEquipmentRunningNumber,
  filterUndefined,
} from 'src/app/lego-utils';
import { isEmpty } from 'lodash-es';
import { combineLatest, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { EcoDataService } from 'src/app/services';
import { EquipmentFacadeService } from 'src/app/services/equipment-facade.service';
import { handleError } from 'src/app/state/akita-error-helper';
import { ControlSpecificationService } from 'src/app/state/control-specification';
import { TechnicalSpecificationService } from 'src/app/state/technical-specification';
import { CollectionState } from 'src/app/state/collection-state';
import { Eco, TaskListHeader } from 'src/app/models/domain';

@Injectable({ providedIn: 'root' })
export class EcoService {
  private readonly state = new CollectionState<Eco>();

  public readonly ecos$ = this.state.items$;
  public readonly loading$ = this.state.loading$;
  public readonly error$ = this.state.error$;

  public constructor(
    private readonly ecoDataService: EcoDataService,
    private readonly equipmentFacade: EquipmentFacadeService,
    private readonly technicalSpecificationService: TechnicalSpecificationService,
    private readonly controlSpecificationService: ControlSpecificationService
  ) {}

  public loadByActiveEquipment() {
    return this.equipmentFacade.selectedEquipmentId$.pipe(
      filterUndefined(),
      tap(() => {
        this.state.setLoading(true);
      }),
      switchMap((equipmentNumber) => {
        const equipmentRunningNumber =
          convertEquipmentNumberToEquipmentRunningNumber(equipmentNumber);

        const dashRunningNumber = `-${equipmentRunningNumber}`;

        const matchesEquipment = (headers: TaskListHeader[]) =>
          headers.filter(({ description }) => description.includes(dashRunningNumber));

        // combineLatest, not zip: the two spec tables page independently, so they emit
        // different numbers of times and zip would pair the Nth of one with the Nth of the
        // other — buffering, then going stale once the counts diverge. Only the latest
        // headers from each matter here.
        return combineLatest([
          this.technicalSpecificationService.taskListHeaders$.pipe(map(matchesEquipment)),
          this.controlSpecificationService.taskListHeaders$.pipe(map(matchesEquipment)),
        ]);
      }),
      map(([technicalSpecificationHeaders, controlSpecificationHeaders]) => {
        const taskListHeaders = technicalSpecificationHeaders.concat(controlSpecificationHeaders);

        return taskListHeaders.map((taskListHeader) => taskListHeader.changeNumber);
      }),
      switchMap((changeNumbers) => {
        if (isEmpty(changeNumbers)) {
          return of([]);
        }

        return catchAndContinue(this.ecoDataService.loadByChangeNumbers(changeNumbers), (error) => {
          handleError(this.state, error);
        });
      }),
      tap((ecos) => {
        this.state.set(ecos);
      }),
      map(() => undefined)
    );
  }
}
